import React from 'react';
import { AgentExecutionFlow, ScoredResult, SimilarityScore, ExecutionEvent, ClarificationRequestedEvent, OrchestratorDecisionEvent } from '../types';
import { ExecutionStepsViewer } from './ExecutionStepsViewer';
import { MessageBubble } from './MessageBubble';
import { EnhancedSearchResultsList } from './EnhancedSearchResultsList';
import { ExecutionScoreCard } from './ExecutionScoreCard';
import { AIExecutionScoreCard } from './AIExecutionScoreCard';
import { ClarificationDialog } from './ClarificationDialog';
import { Loader2, Minimize2, Maximize2, Award, RefreshCw, GitCompare } from 'lucide-react';
import { NoDataCard } from './NoDataCard';

interface ProviderResultColumnProps {
  providerName: string;
  providerId?: string;
  executionFlow: AgentExecutionFlow | undefined;
  isLoading: boolean;
  columnWidth: string;
  isMinimized?: boolean;
  onToggleMinimize?: () => void;
  onRefresh?: () => void;
  scoreData?: ScoredResult;
  similarities?: SimilarityScore[];
  allProviders?: Array<{ executionId: string; displayName: string }>;
  evaluationExecutionId?: string; // For AI scoring
  executionEvents?: ExecutionEvent[]; // For detecting clarification events
  onSubmitClarification?: (executionId: string, userResponse: string) => Promise<void>;
  previousRounds?: Array<{ prompt: string; executionFlow: AgentExecutionFlow | undefined }>;
  currentPrompt?: string;
}

export const ProviderResultColumn: React.FC<ProviderResultColumnProps> = ({
  providerName,
  providerId,
  executionFlow,
  isLoading,
  columnWidth,
  isMinimized = false,
  onToggleMinimize,
  onRefresh,
  scoreData,
  similarities = [],
  allProviders = [],
  evaluationExecutionId,
  executionEvents = [],
  onSubmitClarification,
  previousRounds = [],
  currentPrompt
}) => {
  // Determine if this specific provider is loading
  // Provider is loading if: global loading state OR execution status is inProgress
  const isProviderLoading = isLoading || executionFlow?.status === 'inProgress';
  
  // Check if this is a custom provider (instance ID starts with "custom-")
  const isCustomProvider = providerId?.startsWith('custom-') || providerId === 'custom';
  
  // Get similarities for this provider
  const currentExecutionId = executionFlow?.executionId;
  const outgoingSimilarities = similarities.filter(s => s.fromId === currentExecutionId);

  // Detect clarification events
  const clarificationEvent = executionEvents.find(
    (e): e is ClarificationRequestedEvent => e.eventType === 'ClarificationRequested'
  );
  const clarificationReceived = executionEvents.some(e => e.eventType === 'ClarificationReceived');
  const [clarificationDismissed, setClarificationDismissed] = React.useState(false);
  const [showResuming, setShowResuming] = React.useState(false);

  // Fallback: derive clarification data from OrchestratorDecision when no ClarificationRequested event
  const orchestratorDecisionEvent = executionEvents.find(
    (e): e is OrchestratorDecisionEvent => e.eventType === 'OrchestratorDecision'
  );
  const needsClarificationFromDecision = !clarificationEvent
    && executionFlow?.status === 'needsClarification'
    && orchestratorDecisionEvent?.decision?.decision === 'need_explanation';

  // Unified clarification data — prefer ClarificationRequested, fall back to OrchestratorDecision
  const clarificationData = clarificationEvent
    ? { reason: clarificationEvent.reason, originalQuery: clarificationEvent.originalQuery, questions: clarificationEvent.questions }
    : needsClarificationFromDecision
      ? { reason: orchestratorDecisionEvent!.decision.reason ?? '', originalQuery: orchestratorDecisionEvent!.query, questions: orchestratorDecisionEvent!.decision.questions }
      : null;

  // Show "Resuming..." once when clarification is received; hide on next event or terminal status
  const resumingEventsCount = React.useRef(executionEvents.length);
  const resumingDone = React.useRef(false);
  React.useEffect(() => {
    if (clarificationReceived && clarificationData && !clarificationDismissed && !resumingDone.current) {
      setShowResuming(true);
      resumingEventsCount.current = executionEvents.length;
    }
  }, [clarificationReceived, clarificationData, clarificationDismissed]);

  // Hide resuming when any new event arrives or execution reaches a terminal status
  React.useEffect(() => {
    if (!showResuming) return;
    const isTerminal = executionFlow?.status && executionFlow.status !== 'inProgress' && executionFlow.status !== 'needsClarification';
    const hasNewEvents = executionEvents.length > resumingEventsCount.current;
    if (isTerminal || hasNewEvents) {
      setShowResuming(false);
      resumingDone.current = true;
    }
  }, [showResuming, executionEvents.length, executionFlow?.status]);

  const showClarificationDialog = clarificationData && !clarificationReceived && !clarificationDismissed;

  const handleClarificationSubmit = async (response: string) => {
    if (onSubmitClarification && executionFlow?.executionId) {
      await onSubmitClarification(executionFlow.executionId, response);
    }
  };

  const handleClarificationCancel = () => {
    setClarificationDismissed(true);
  };
  
  // Helper to format percentage
  const formatPercent = (value: number) => `${Math.round(value * 100)}%`;
  
  // Helper to get accuracy color
  const getAccuracyColor = (accuracy: number) => {
    if (accuracy >= 0.90) return 'bg-green-100 text-green-700 border-green-300';
    if (accuracy >= 0.75) return 'bg-yellow-100 text-yellow-700 border-yellow-300';
    return 'bg-red-100 text-red-700 border-red-300';
  };
  
  // Helper to get similarity color
  const getSimilarityColor = (similarity: number) => {
    if (similarity >= 0.80) return 'text-green-600';
    if (similarity >= 0.60) return 'text-yellow-600';
    if (similarity >= 0.40) return 'text-orange-600';
    return 'text-red-600';
  };
  
  const getStatusColor = (status?: string) => {
    if (!status) return 'bg-gray-100 text-gray-700';
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-700';
      case 'failed':
        return 'bg-red-100 text-red-700';
      case 'inProgress':
        return 'bg-blue-100 text-blue-700';
      case 'blocked':
        return 'bg-yellow-100 text-yellow-700';
      case 'needsClarification':
        return 'bg-orange-100 text-orange-700';
      case 'noData':
        return 'bg-blue-100 text-blue-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  if (isMinimized) {
    return (
      <div className="flex-shrink-0 border-r border-gray-200 bg-white flex flex-col h-full" style={{ width: columnWidth }}>
        {/* Minimized Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-2 z-10 flex flex-col items-center gap-2">
          <button
            onClick={onToggleMinimize}
            className="p-1.5 hover:bg-gray-100 rounded transition-colors"
            title="Maximize"
          >
            <Maximize2 className="w-4 h-4 text-gray-600" />
          </button>
          <div className="writing-mode-vertical text-sm font-semibold text-gray-900 whitespace-nowrap" style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>
            {providerName}
          </div>
          {executionFlow && (
            <div className={`w-2 h-2 rounded-full ${getStatusColor(executionFlow.status).replace('text-', 'bg-').replace('-700', '-500')}`} title={executionFlow.status} />
          )}
        </div>
      </div>
    );
  }

  return (
    <div data-component="ProviderResultColumn" className="flex-shrink-0 border-r border-gray-200 bg-white flex flex-col h-full" style={{ width: columnWidth }}>
      {/* Provider Header */}
      <div className="sticky top-0 bg-white border-b border-gray-200 p-4 z-10">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 flex-wrap">
            {isCustomProvider ? (
              <div className="flex items-center gap-2 px-2 py-0.5 bg-purple-100 rounded">
                <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <h3 className="text-lg font-semibold text-purple-900">{providerName}</h3>
              </div>
            ) : (
              <h3 className="text-lg font-semibold text-gray-900">{providerName}</h3>
            )}
            {executionFlow && (
              <div className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(executionFlow.status)}`}>
                {executionFlow.status}
              </div>
            )}
            {scoreData && (
              <div className="flex items-center gap-2">
                <div className={`px-3 py-1.5 rounded-lg text-sm font-bold flex items-center gap-1.5 border ${getAccuracyColor(scoreData.accuracy)}`}>
                  <Award className="w-3.5 h-3.5" />
                  {formatPercent(scoreData.accuracy)}
                </div>
                <span className="text-xs text-gray-500 font-medium">Rank #{scoreData.rank}</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-1">
            {onRefresh && (
              <button
                onClick={onRefresh}
                disabled={isProviderLoading}
                className="p-1.5 hover:bg-gray-100 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Refresh this provider"
              >
                <RefreshCw className={`w-4 h-4 text-gray-600 ${isProviderLoading ? 'animate-spin' : ''}`} />
              </button>
            )}
            {onToggleMinimize && (
              <button
                onClick={onToggleMinimize}
                className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                title="Minimize"
              >
                <Minimize2 className="w-4 h-4 text-gray-600" />
              </button>
            )}
          </div>
        </div>
        
        {/* Accuracy Reasoning */}
        {scoreData && scoreData.reasoning && (
          <div data-flow="accuracy-reasoning" className="mt-2 text-xs text-gray-600 bg-gray-50 rounded p-2">
            <span className="font-medium">Accuracy Evaluation:</span> {scoreData.reasoning}
          </div>
        )}

        {/* Gap Analysis */}
        {scoreData && scoreData.gapAnalysis && (
          <div data-flow="gap-analysis" className="mt-2 text-xs text-amber-700 bg-amber-50 rounded p-2 border border-amber-200">
            <span className="font-medium">Missing for 100%:</span> {scoreData.gapAnalysis}
          </div>
        )}
        
        {/* Similarity Scores */}
        {outgoingSimilarities.length > 0 && (
          <div data-flow="similarity-scores" className="mt-3 border-t border-gray-200 pt-3">
            <div className="flex items-center gap-2 mb-2">
              <GitCompare className="w-3.5 h-3.5 text-gray-500" />
              <span className="text-xs font-semibold text-gray-700">Similarity to Other Responses</span>
            </div>
            <div className="space-y-1.5">
              {outgoingSimilarities.map(sim => {
                const targetProvider = allProviders.find(p => p.executionId === sim.toId);
                return (
                  <div key={sim.toId} className="flex items-center justify-between text-xs bg-gray-50 rounded px-2 py-1.5">
                    <span className="text-gray-700 font-medium truncate flex-1 mr-2">
                      {targetProvider?.displayName || sim.toId}
                    </span>
                    <span className={`font-bold ${getSimilarityColor(sim.score)}`}>
                      {formatPercent(sim.score)}
                    </span>
                  </div>
                );
              })}
            </div>
            <div className="mt-2 text-xs text-gray-500 italic">
              Shows how much of this response's content appears in others
            </div>
          </div>
        )}
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Previous conversation rounds */}
        {previousRounds.map((round, idx) => (
          <div key={idx} data-flow="previous-round" className="space-y-4">
            {/* User prompt bubble */}
            <div className="flex items-start gap-2">
              <div className="w-7 h-7 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div className="flex-1 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                <p className="text-sm text-gray-800 whitespace-pre-wrap">{round.prompt}</p>
              </div>
            </div>
            {/* Previous round's execution results */}
            {round.executionFlow && (
              <>
                <ExecutionStepsViewer executionFlow={round.executionFlow} />
                {round.executionFlow.finalResponse?.searchResults && (
                  <EnhancedSearchResultsList searchResults={round.executionFlow.finalResponse.searchResults} />
                )}
                {round.executionFlow.finalResponse?.answer && round.executionFlow.status !== 'noData' && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">AI Response</h4>
                    <MessageBubble
                      message={{
                        id: round.executionFlow.executionId,
                        type: 'assistant',
                        content: round.executionFlow.finalResponse.answer,
                        timestamp: new Date(round.executionFlow.endTime || round.executionFlow.startTime),
                        isComplete: true,
                        executionId: round.executionFlow.executionId
                      }}
                      onCopy={() => {}}
                      isCopied={false}
                      citations={round.executionFlow.finalResponse.citations}
                      paragraphs={round.executionFlow.finalResponse.paragraphs}
                      opinions={round.executionFlow.searchedOpinions}
                    />
                  </div>
                )}
                {round.executionFlow.status === 'noData' && (
                  <NoDataCard
                    status={round.executionFlow.finalResponse?.status as 'no_data' | 'no_relevant_data' | 'no_relevant_results' || 'no_data'}
                    message={round.executionFlow.finalResponse?.message}
                    answer={round.executionFlow.finalResponse?.answer}
                  />
                )}
              </>
            )}
            {/* Separator between rounds */}
            <div className="border-t border-gray-200" />
          </div>
        ))}

        {/* Current round user prompt bubble */}
        {currentPrompt && (
          <div data-flow="current-prompt" className="flex items-start gap-2">
            <div className="w-7 h-7 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0 mt-0.5">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <div className="flex-1 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
              <p className="text-sm text-gray-800 whitespace-pre-wrap">{currentPrompt}</p>
            </div>
          </div>
        )}

        {isProviderLoading && !executionFlow && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            <span className="ml-3 text-gray-600">Processing...</span>
          </div>
        )}

        {executionFlow && (
          <>
            {/* Execution Flow Viewer */}
            <ExecutionStepsViewer executionFlow={executionFlow} />

            {/* Clarification Dialog */}
            {showClarificationDialog && (
              <ClarificationDialog
                executionId={executionFlow.executionId}
                reason={clarificationData.reason}
                originalQuery={clarificationData.originalQuery}
                questions={clarificationData.questions}
                onSubmit={handleClarificationSubmit}
                onCancel={handleClarificationCancel}
              />
            )}

            {/* Resuming indicator after clarification submitted */}
            {showResuming && (
              <div data-flow="resuming" className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg my-3">
                <Loader2 className="w-4 h-4 animate-spin text-green-600" />
                <span className="text-sm text-green-700 font-medium">Resuming...</span>
              </div>
            )}

            {/* Search Results - This includes opinions */}
            {executionFlow.finalResponse?.searchResults && (
              <div className="mt-4">
                <EnhancedSearchResultsList 
                  searchResults={executionFlow.finalResponse.searchResults}
                />
              </div>
            )}

            {/* Final Response (AI Answer) - hidden for noData status since NoDataCard handles display */}
            {executionFlow.finalResponse && executionFlow.finalResponse.answer && executionFlow.status !== 'noData' && (
              <div className="mt-4">
                <h4 className="font-medium text-gray-900 mb-2">AI Response</h4>
                <MessageBubble
                  message={{
                    id: executionFlow.executionId,
                    type: 'assistant',
                    content: executionFlow.finalResponse.answer,
                    timestamp: new Date(executionFlow.endTime || executionFlow.startTime),
                    isComplete: true,
                    executionId: executionFlow.executionId
                  }}
                  onCopy={() => {}}
                  isCopied={false}
                  citations={executionFlow.finalResponse.citations}
                  paragraphs={executionFlow.finalResponse.paragraphs}
                  opinions={executionFlow.searchedOpinions}
                />
              </div>
            )}

            {/* Execution Score Card */}
            {executionFlow.status?.toLowerCase() === 'completed' && executionFlow.executionId && (
              <div className="mt-4">
                <ExecutionScoreCard executionId={executionFlow.executionId} />
              </div>
            )}

            {/* AI Execution Score Card - Show for evaluation executions */}
            {executionFlow.status?.toLowerCase() === 'completed' && evaluationExecutionId && providerId && (
              <div className="mt-4">
                <AIExecutionScoreCard 
                  evaluationExecutionId={evaluationExecutionId}
                  providerId={providerId}
                />
              </div>
            )}

            {/* Needs Clarification State (non-HITL) */}
            {executionFlow.status === 'needsClarification' && !showClarificationDialog && (
              <div data-flow="needs-clarification" className="bg-amber-50 border border-amber-300 rounded-lg p-4 mt-4">
                <div className="flex items-start gap-3">
                  <span className="text-xl mt-0.5">❓</span>
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-amber-900 mb-1">Clarification Needed</div>
                    <div className="text-sm text-amber-800">
                      {executionFlow.finalResponse?.message || 'The system needs additional information to complete this request.'}
                    </div>
                    <div className="mt-2 text-xs text-amber-600">
                      Please refine your query above with the requested details.
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Error or Blocked Messages */}
            {executionFlow.finalResponse && executionFlow.finalResponse.message && 
             (executionFlow.status === 'blocked' || executionFlow.status === 'failed') && (
              <div className={`p-4 rounded-lg ${
                executionFlow.status === 'blocked' ? 'bg-yellow-50 border border-yellow-200' : 
                'bg-red-50 border border-red-200'
              }`}>
                <p className="text-sm">{executionFlow.finalResponse.message}</p>
              </div>
            )}

            {/* No Data State */}
            {executionFlow.status === 'noData' && (
              <div data-flow="no-data">
                <NoDataCard
                  status={executionFlow.finalResponse?.status as 'no_data' | 'no_relevant_data' | 'no_relevant_results' || 'no_data'}
                  message={executionFlow.finalResponse?.message}
                  answer={executionFlow.finalResponse?.answer}
                />
              </div>
            )}

            {/* Loading indicator at bottom when still processing */}
            {isProviderLoading && executionFlow.status === 'inProgress' && (
              <div className="flex items-center justify-center py-6 border-t border-gray-200">
                <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                <span className="ml-3 text-gray-600">Processing...</span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
